import { useFlags } from "flagsmith/react";
import type { IFlagsmithFeature } from "flagsmith/types";
import { useHidePmCloseTabs } from "hooks/useHidePmCloseTabs";
import type { ReactElement } from "react";
import { Navigate } from "react-router-dom";

interface Props {
	/** What the address shows today, while `hide_pm_close_tabs` is on. */
	legacy: ReactElement;
	/** What it shows once the close tabs live inside Project Management. */
	tabs: ReactElement;
	/**
	 * The address shows the close and nothing else (the close template
	 * library), so `hide_dashboard` sends it home in both releases.
	 */
	isCloseSurface?: boolean;
}

/**
 * One address, two releases. With `hide_pm_close_tabs` on (the default) the
 * address behaves exactly as it did under `CloseTrackerGate`: the close surface,
 * or home when `hide_dashboard` hides the close. With it off the address
 * belongs to Project Management, which decides for itself what
 * `hide_dashboard` leaves out, so no gate applies here; an address that is a
 * close surface on its own (`isCloseSurface`) still goes home.
 */
export function PmCloseTabsRoute({
	legacy,
	tabs,
	isCloseSurface = false,
}: Props) {
	const isTabsHidden = useHidePmCloseTabs();
	const flags = useFlags(["hide_dashboard"]);
	const isCloseHidden = !!(flags.hide_dashboard as IFlagsmithFeature)?.enabled;

	const home = <Navigate to="/" replace />;
	if (!isTabsHidden) return isCloseHidden && isCloseSurface ? home : tabs;
	return isCloseHidden ? home : legacy;
}
